/**
 * SiteGenerationOverlay — the full-screen "AI is building your website" state.
 *
 * Presentation only: it renders the staged progress produced by
 * `useSiteGeneration`. It follows the app's existing overlay idiom (fixed,
 * scroll-locked, navy scrim, framer-motion) established by PhoneVerificationGate.
 */
import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Check, Loader2, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { BRAND } from '../../config'
import { GENERATION_STEPS } from '../hooks/useSiteGeneration'

interface Props {
  open: boolean
  stepIndex: number
  progress: number
  businessName: string
}

export function SiteGenerationOverlay({ open, stepIndex, progress, businessName }: Props) {
  const { t } = useTranslation('builder')

  // Lock body scroll while the overlay is visible.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[2000] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(1,17,79,0.88)', backdropFilter: 'blur(6px)' }}
          role="dialog"
          aria-modal="true"
          aria-label={t('generating.title')}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl"
          >
            {/* Animated AI mark */}
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <motion.div
                  className="flex h-16 w-16 items-center justify-center rounded-2xl text-white"
                  style={{ background: `linear-gradient(135deg, ${BRAND.red}, ${BRAND.navy})` }}
                  animate={{ scale: [1, 1.06, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <Sparkles className="h-7 w-7" />
                </motion.div>
              </div>
            </div>

            <h2 className="text-center text-lg font-bold text-gray-900">
              {t('generating.title')}
            </h2>
            <p className="mt-1 text-center text-sm text-gray-500">
              {t('generating.subtitle', { businessName })}
            </p>

            {/* Progress bar */}
            <div className="mt-6">
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: `linear-gradient(90deg, ${BRAND.red}, ${BRAND.navy})` }}
                  animate={{ width: `${progress}%` }}
                  transition={{ ease: 'easeOut', duration: 0.3 }}
                />
              </div>
              <p className="mt-2 text-right text-xs font-medium tabular-nums text-gray-400">
                {progress}%
              </p>
            </div>

            {/* Staged step list */}
            <ul className="mt-4 space-y-2.5">
              {GENERATION_STEPS.map((step, i) => {
                const done = i < stepIndex
                const active = i === stepIndex
                return (
                  <li key={step.key} className="flex items-center gap-3">
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors"
                      style={{
                        backgroundColor: done || active ? BRAND.red : '#f3f4f6',
                        color: done || active ? '#fff' : '#9ca3af',
                      }}
                    >
                      {done ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : active ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <span className="text-[11px] font-semibold">{i + 1}</span>
                      )}
                    </span>
                    <span
                      className={`text-sm transition-colors ${
                        active ? 'font-semibold text-gray-900' : done ? 'text-gray-500' : 'text-gray-400'
                      }`}
                    >
                      {t(`generating.steps.${step.key}`)}
                    </span>
                  </li>
                )
              })}
            </ul>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
